import { State } from '@meraki-internal/state';
import { RevenueCatAPIModel } from './revenue-cat/RevenueCatAPIModel';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../support/AsyncStorageProvider';
import { ISO8601 } from '../support/ISO8601';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { isPlatform } from '../utils/isPlatform';

export type ITriggerPaywallSource = 'menu logo' | 'menu button' | 'care map limit' | 'attachment limit' | 'co-owner invite' | 'subscription expired' | 'connect calendar' | 'first questionnaire completed' | 'menu add appointment' | 'add appointment' | 'quick add appointment' | 'recurring' | 'recordings' | 'recording ai processing' | 'welcome';

export class InnerhivePlusDialogViewModel extends State<Record<string, never>> {

    private _isOpen = false;
    private _isWorking = false;
    private lastSeenStorage: IAsyncSmartStorageProvider<ISO8601>;

    LAST_SEEN_STORAGE_KEY = 'paywall.last-seen';

    static inject = () => [RevenueCatModel, MixpanelService, AppPrincipal, AsyncStorageProvider];
    constructor(
        private revenueCat: RevenueCatAPIModel,
        private tracking: MixpanelService,
        private principal: AppPrincipal,
        storageProvider: AsyncStorageProvider
    ) {
        super({ });
        this.lastSeenStorage = storageProvider.getStringProvider(this.LAST_SEEN_STORAGE_KEY);
    }

    isWorking = () => this._isWorking;

    setWorking = (working: boolean) => {
        this._isWorking = working;
        this.setState({});
    };

    isOpen = () => this._isOpen;

    open = ({ triggerSource }: { triggerSource: ITriggerPaywallSource }) => {

        // if we're already subscribed, this doesn't count as a paywall event
        if (!this.revenueCat.hasInnerhivePlus()) {
            this.lastSeenStorage.set((new Date()).toISOString());
            this.tracking.track('Paywall Triggered', () => ({
                Location: triggerSource,
                'Free Care Map Tier Limit': this.revenueCat.getOfferMeta().freeCaremapsOwned,
                'Free Attachment Tier Limit': this.revenueCat.getOfferMeta().freeAttachmentsPerCaremap,
                'Free Appointment Tier Limit': this.revenueCat.getOfferMeta().freeAppointmentsPerCaremap,
                'Free Co-Owner Tier Limit': 0,
                Offering: this.revenueCat.getOfferMeta().offering_id
            }));
        }

        this._isOpen = true;
        this.setState({ });
    };

    resume = async ({ triggerSource }: { triggerSource: ITriggerPaywallSource }): Promise<boolean> => {
        if (isAnonymousViewer(this.principal)) {
            console.warn('Cannot resume upgrade b/c anonymous user');
            return false;
        }

        if (this.revenueCat.hasInnerhivePlus()) {
            console.warn('Cannot resume upgrade b/c already upgraded');
            return true;
        }

        return new Promise(async resolve => {
            try {
                this._isWorking = true;
                this.open({ triggerSource });

                let unsubscribe: (() => void) | undefined = undefined;
                unsubscribe = this.subscribe(() => {
                    if (!this.isOpen()) {
                        resolve(this.revenueCat.hasInnerhivePlus());
                        if (unsubscribe) {
                            unsubscribe();
                        }
                    }
                });

                // auto-launch the os-level store purchase dialog
                if (isPlatform('capacitor')) {
                    await this.revenueCat.upgrade();
                }

            } catch (e) {
                // assume it's clear to the user what went wrong
                // from the messaging in the purchase dialog
            } finally {
                this._isWorking = false;
                this.setState({});
            }
        });
    };

    close = () => {
        this._isOpen = false;
        this.setState({ });
    };

    hasUpgraded = () => this.revenueCat.hasInnerhivePlus();

    requirePurchase = async ({ triggerSource }: { triggerSource: ITriggerPaywallSource;  }) => {
        return new Promise((resolve, reject) => {
            try {
                this.open({ triggerSource });
                const unsubscribe = this.subscribe(() => {
                    if (!this.isOpen()) {
                        unsubscribe();
                        resolve(this.revenueCat.hasInnerhivePlus());
                    }
                });
            } catch (e) {
                reject(e);
            }
        });
    };
}
